import React, { useState, useEffect } from 'react';
import { Plus, CreditCard, Pencil, Trash2, CheckCircle2, Wallet, ShieldCheck, XCircle } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { billing as api, units as unitsApi, residents as residentsApi, type Invoice, type Unit, type Resident, type ApiError } from '../../services/api';
import DataTable from '../../components/DataTable';
import SearchSelect from '../../components/SearchSelect';
import { useServerTable } from '../../hooks/useServerTable';

declare global {
  interface Window {
    Razorpay: new (options: Record<string, unknown>) => { open: () => void };
  }
}

let razorpayScriptPromise: Promise<boolean> | null = null;
function loadRazorpayScript(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise(resolve => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};
const actionBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: '4px 6px', borderRadius: 6, color: 'var(--nx-text-muted)',
  display: 'inline-flex', alignItems: 'center',
};

const CREATE_INIT = { unitId: '', residentId: '', amount: '', dueDate: '' };
const EDIT_INIT   = { amount: '', dueDate: '', status: 'unpaid' };

const normStatus = (s: string | undefined) => (s ?? '').toLowerCase();

const statusColor = (raw: string | undefined) => {
  const s = normStatus(raw);
  return s === 'paid'    ? { bg: 'rgba(16,185,129,0.1)',  text: '#10B981' }
    :    s === 'unpaid'  ? { bg: 'rgba(245,158,11,0.1)',  text: '#D97706' }
    :    s === 'overdue' ? { bg: 'rgba(239,68,68,0.1)',   text: '#DC2626' }
    :                      { bg: 'rgba(148,163,184,0.1)', text: '#64748B' };
};

const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const {
    rows: invoices, totalElements, totalPages, loading,
    page, setPage, pageSize, setPageSize,
    sortField, sortDir, setSort, refetch,
  } = useServerTable<Invoice>(
    params => societyId
      ? api.listInvoicesPage(societyId, params)
      : Promise.resolve({ content: [], totalElements: 0, totalPages: 0 }),
    [societyId],
  );

  const [unitList, setUnitList] = useState<Unit[]>([]);
  const [residentList, setResidentList] = useState<Resident[]>([]);

  // create
  const [showCreate, setShowCreate]   = useState(false);
  const [creating, setCreating]       = useState(false);
  const [createErr, setCreateErr]     = useState('');
  const [createForm, setCreateForm]   = useState(CREATE_INIT);

  // edit
  const [editTarget, setEditTarget]   = useState<Invoice | null>(null);
  const [editForm, setEditForm]       = useState(EDIT_INIT);
  const [saving, setSaving]           = useState(false);
  const [editErr, setEditErr]         = useState('');

  // delete
  const [deleteTarget, setDeleteTarget] = useState<Invoice | null>(null);
  const [deleting, setDeleting]         = useState(false);

  // mark paid
  const [payingId, setPayingId]       = useState<string | null>(null);

  // claimed payment confirm/reject
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId]   = useState<string | null>(null);

  // pay online
  const [payOnlineId, setPayOnlineId] = useState<string | null>(null);
  const [payOnlineError, setPayOnlineError] = useState('');

  useEffect(() => {
    if (!societyId) return;
    unitsApi.list(societyId, { size: 500 }).then(page => setUnitList(page.content)).catch(() => {});
    residentsApi.list(societyId, { size: 500 }).then(page => setResidentList(page.content)).catch(() => {});
  }, [societyId]);

  const unitLabel = (u: Unit) =>
    u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber;

  // Preview only — the real invoice number is assigned by the backend (per-society,
  // per-year sequential counter) at save time, since that's the only place concurrent
  // creates can be serialized safely. This just shows the admin what to expect.
  const invoiceNumberPreview = `INV-${new Date().getFullYear()}-${String(totalElements + 1).padStart(5, '0')}`;

  /* ── create ── */
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setCreating(true); setCreateErr('');
    try {
      await api.createInvoice(societyId, {
        unitId: createForm.unitId,
        residentId: createForm.residentId || undefined,
        amount: Number(createForm.amount), dueDate: createForm.dueDate,
      });
      refetch();
      setShowCreate(false); setCreateForm(CREATE_INIT);
    } catch (err) {
      setCreateErr((err as ApiError).message || 'Failed to create invoice');
    } finally { setCreating(false); }
  };

  /* ── edit ── */
  const openEdit = (inv: Invoice) => {
    setEditTarget(inv);
    setEditForm({
      amount: String(inv.amount ?? ''),
      dueDate: inv.dueDate ?? '',
      status: normStatus(inv.status) || 'unpaid',
    });
    setEditErr('');
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !editTarget) return;
    setSaving(true); setEditErr('');
    try {
      await api.updateInvoice(societyId, editTarget.id, {
        amount: Number(editForm.amount),
        dueDate: editForm.dueDate,
        status: editForm.status,
      } as Partial<Invoice>);
      refetch();
      setEditTarget(null);
    } catch (err) {
      setEditErr((err as ApiError).message || 'Failed to update invoice');
    } finally { setSaving(false); }
  };

  /* ── delete ── */
  const handleDelete = async () => {
    if (!societyId || !deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteInvoice(societyId, deleteTarget.id);
      refetch();
      setDeleteTarget(null);
    } catch { /* ignore */ } finally { setDeleting(false); }
  };

  /* ── mark paid ── */
  const markPaid = async (id: string) => {
    if (!societyId) return;
    setPayingId(id);
    try {
      await api.payInvoice(societyId, id);
      refetch();
    } catch { /* ignore */ } finally { setPayingId(null); }
  };

  /* ── confirm / reject claimed payment ── */
  const confirmClaim = async (id: string) => {
    if (!societyId) return;
    setConfirmingId(id);
    try {
      await api.confirmClaimedPayment(societyId, id);
      refetch();
    } catch { /* ignore */ } finally { setConfirmingId(null); }
  };

  const rejectClaim = async (id: string) => {
    if (!societyId) return;
    setRejectingId(id);
    try {
      await api.rejectClaimedPayment(societyId, id);
      refetch();
    } catch { /* ignore */ } finally { setRejectingId(null); }
  };

  /* ── pay online (Razorpay) ── */
  const handlePayOnline = async (inv: Invoice) => {
    if (!societyId) return;
    setPayOnlineId(inv.id); setPayOnlineError('');
    try {
      const order = await api.createPaymentOrder(societyId, inv.id);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setPayOnlineError('Failed to load payment gateway. Please try again.');
        setPayOnlineId(null);
        return;
      }
      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: Math.round(order.amount * 100),
        currency: order.currency,
        name: 'Nexora',
        description: `Invoice ${inv.invoiceNumber}`,
        order_id: order.orderId,
        handler: async (response: { razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            await api.verifyPayment(societyId, inv.id, {
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            refetch();
          } catch (err) {
            setPayOnlineError((err as ApiError).message || 'Payment verification failed.');
          } finally {
            setPayOnlineId(null);
          }
        },
        modal: { ondismiss: () => setPayOnlineId(null) },
      });
      rzp.open();
    } catch (err) {
      setPayOnlineError((err as ApiError).message || 'Failed to start online payment.');
      setPayOnlineId(null);
    }
  };

  const fmtAmount = (n: number | undefined) =>
    `₹${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Billing
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{totalElements} invoices</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Create Invoice
        </button>
      </div>

      {payOnlineError && (
        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, fontSize: 12, color: '#EF4444', marginBottom: 16 }}>
          {payOnlineError}
        </div>
      )}

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataTable
          columns={[
            {
              key: 'invoice', header: 'Invoice #',
              cellStyle: { color: 'var(--nx-blue)', fontWeight: 500, fontFamily: 'monospace', fontSize: 11 },
              sortable: true, sortKey: 'invoiceNumber',
              render: (inv: Invoice) => inv.invoiceNumber,
            },
            {
              key: 'unit', header: 'Block / Unit',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              sortable: true, sortKey: 'u.unitNumber',
              render: (inv: Invoice) =>
                inv.unitNumber
                  ? (inv.blockName ? `${inv.blockName} — ${inv.unitNumber}` : inv.unitNumber)
                  : '—',
            },
            {
              key: 'resident', header: 'Resident',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              sortable: true, sortKey: 'r.fullName',
              render: (inv: Invoice) => inv.residentName || '—',
            },
            {
              key: 'amount', header: 'Amount',
              cellStyle: { fontWeight: 600 },
              sortable: true, sortKey: 'amount',
              render: (inv: Invoice) => fmtAmount(inv.amount),
            },
            {
              key: 'gst', header: 'GST',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (inv: Invoice) => {
                const tax = (inv.cgstAmount ?? 0) + (inv.sgstAmount ?? 0) + (inv.igstAmount ?? 0);
                return tax > 0
                  ? <span title={`CGST ${fmtAmount(inv.cgstAmount)} + SGST ${fmtAmount(inv.sgstAmount)}`}>{fmtAmount(tax)}</span>
                  : '—';
              },
            },
            {
              key: 'lateFee', header: 'Late Fee',
              cellStyle: { color: '#DC2626' },
              sortable: true, sortKey: 'lateFeeAmount',
              render: (inv: Invoice) => (inv.lateFeeAmount ?? 0) > 0 ? fmtAmount(inv.lateFeeAmount) : '—',
            },
            {
              key: 'total', header: 'Total',
              cellStyle: { fontWeight: 600, color: 'var(--nx-text-primary)' },
              sortable: true, sortKey: 'totalAmount',
              render: (inv: Invoice) => fmtAmount(inv.totalAmount ?? inv.amount),
            },
            {
              key: 'due', header: 'Due Date',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              sortable: true, sortKey: 'dueDate',
              render: (inv: Invoice) => inv.dueDate,
            },
            {
              key: 'status', header: 'Status',
              sortable: true, sortKey: 'status',
              render: (inv: Invoice) => {
                const s = normStatus(inv.status);
                const sc = statusColor(s);
                const claimed = s !== 'paid' && !!inv.paymentClaimedAt;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                      {s ? s.charAt(0).toUpperCase() + s.slice(1) : '—'}
                    </span>
                    {claimed && (
                      <span
                        title={inv.paymentClaimedNote || 'Resident reported this as paid'}
                        style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: 'rgba(245,158,11,0.1)', color: '#D97706' }}
                      >
                        Claimed
                      </span>
                    )}
                  </div>
                );
              },
            },
            {
              key: 'paidAt', header: 'Paid At',
              cellStyle: { color: 'var(--nx-text-muted)' },
              sortable: true, sortKey: 'paidAt',
              render: (inv: Invoice) => inv.paidAt ? new Date(inv.paidAt).toLocaleDateString('en-IN') : '—',
            },
            {
              key: 'actions', header: 'Actions',
              render: (inv: Invoice) => {
                const s = normStatus(inv.status);
                const isPaid = s === 'paid';
                const claimed = !isPaid && !!inv.paymentClaimedAt;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {claimed && (
                      <button
                        onClick={() => confirmClaim(inv.id)}
                        disabled={confirmingId === inv.id || rejectingId === inv.id}
                        style={{ fontSize: 10, padding: '3px 8px', background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <ShieldCheck size={11} />
                        {confirmingId === inv.id ? '…' : 'Confirm'}
                      </button>
                    )}
                    {claimed && (
                      <button
                        onClick={() => rejectClaim(inv.id)}
                        disabled={confirmingId === inv.id || rejectingId === inv.id}
                        style={{ fontSize: 10, padding: '3px 8px', background: 'rgba(239,68,68,0.1)', color: '#DC2626', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <XCircle size={11} />
                        {rejectingId === inv.id ? '…' : 'Reject'}
                      </button>
                    )}
                    {!isPaid && !claimed && (
                      <button
                        onClick={() => handlePayOnline(inv)}
                        disabled={payOnlineId === inv.id}
                        style={{ fontSize: 10, padding: '3px 8px', background: 'rgba(26,108,246,0.1)', color: '#1A6CF6', border: '1px solid rgba(26,108,246,0.2)', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <Wallet size={11} />
                        {payOnlineId === inv.id ? '…' : 'Pay Online'}
                      </button>
                    )}
                    {!isPaid && !claimed && (
                      <button
                        onClick={() => markPaid(inv.id)}
                        disabled={payingId === inv.id}
                        style={{ fontSize: 10, padding: '3px 8px', background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <CheckCircle2 size={11} />
                        {payingId === inv.id ? '…' : 'Mark Paid'}
                      </button>
                    )}
                    <button
                      title="Edit invoice"
                      onClick={() => openEdit(inv)}
                      style={{ ...actionBtn, opacity: isPaid ? 0.4 : 1 }}
                    >
                      <Pencil size={13} />
                    </button>
                    {!isPaid && (
                      <button
                        title="Delete invoice"
                        onClick={() => setDeleteTarget(inv)}
                        style={{ ...actionBtn, color: 'var(--nx-red)' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                );
              },
            },
          ]}
          rows={invoices}
          rowKey={inv => inv.id}
          loading={loading}
          emptyIcon={<CreditCard size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No invoices created yet."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={setSort}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
      </div>

      {/* Create modal */}
      {showCreate && (
        <Modal
          title="Create Invoice" submitLabel="Create Invoice" error={createErr} isLoading={creating}
          onClose={() => { setShowCreate(false); setCreateForm(CREATE_INIT); setCreateErr(''); }}
          onSubmit={handleCreate}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Invoice Number</label>
              <input name="invoiceNumber" value={invoiceNumberPreview}
                disabled readOnly title="Auto-generated when the invoice is created"
                style={{ ...inp, opacity: 0.7, cursor: 'not-allowed' }} />
            </div>
            <div>
              <label style={lbl}>Amount (₹) *</label>
              <input type="number" name="amount" value={createForm.amount}
                onChange={e => setCreateForm(p => ({ ...p, amount: e.target.value }))}
                required placeholder="5000" min="0" step="0.01" style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Unit *</label>
            <SearchSelect
              value={createForm.unitId}
              onChange={v => setCreateForm(p => ({ ...p, unitId: v }))}
              options={unitList.map(u => ({ value: u.id, label: unitLabel(u) }))}
              placeholder={unitList.length === 0 ? 'No units available' : 'Search units…'}
              required
              disabled={unitList.length === 0}
            />
          </div>
          <div>
            <label style={lbl}>Resident</label>
            <SearchSelect
              value={createForm.residentId}
              onChange={v => setCreateForm(p => ({ ...p, residentId: v }))}
              options={residentList
                .filter(r => !createForm.unitId || r.unitId === createForm.unitId)
                .map(r => ({ value: r.id, label: r.fullName }))}
              placeholder="No specific resident (billed to the unit)"
              emptyText="No matching residents"
            />
          </div>
          <div>
            <label style={lbl}>Due Date *</label>
            <input type="date" name="dueDate" value={createForm.dueDate}
              onChange={e => setCreateForm(p => ({ ...p, dueDate: e.target.value }))}
              required style={inp} />
          </div>
        </Modal>
      )}

      {/* Edit modal */}
      {editTarget && (
        <Modal
          title={`Edit Invoice — ${editTarget.invoiceNumber}`}
          submitLabel="Save Changes"
          error={editErr}
          isLoading={saving}
          onClose={() => setEditTarget(null)}
          onSubmit={handleEdit}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Amount (₹) *</label>
              <input type="number" value={editForm.amount} min="0" step="0.01" required style={inp}
                onChange={e => setEditForm(p => ({ ...p, amount: e.target.value }))} />
            </div>
            <div>
              <label style={lbl}>Due Date *</label>
              <input type="date" value={editForm.dueDate} required style={inp}
                onChange={e => setEditForm(p => ({ ...p, dueDate: e.target.value }))} />
            </div>
          </div>
          <div>
            <label style={lbl}>Status</label>
            <select value={editForm.status} style={inp}
              onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="partial">Partial</option>
            </select>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <ConfirmDialog
          open
          title="Delete Invoice"
          message={`Delete invoice ${deleteTarget.invoiceNumber} (${fmtAmount(deleteTarget.amount)})? This cannot be undone.`}
          confirmLabel="Delete"
          isLoading={deleting}
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </PageLayout>
  );
};

export default BillingPage;
